import { useGetStaffLevelsPaginated } from "@/hooks/staff/use-staff-levels";
import {
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  MRT_ToggleFiltersButton,
} from "material-react-table";
import { useMemo } from "react";

import { APIPaginatedTable } from "@/components/common/api-paginated-table";
import type { APIPaginatedTableState } from "@/components/common/api-paginated-table/states";
import type { StaffLevel } from "@/types/staff/api-types/staff-level-types";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";

const createStaffLevelsFilters = (columnFilters: MRT_ColumnFiltersState) => {
  const filters: {
    name?: string;
  } = {};

  const getTrimmedString = (value: unknown) =>
    typeof value === "string" ? value.trim() : "";

  columnFilters.forEach((filter) => {
    const colName = filter.id.split(".")[1] ?? filter.id;
    const trimmedValue = getTrimmedString(filter.value);
    if (trimmedValue === "") return;

    if (colName === "name") {
      filters.name = trimmedValue;
    }
  });

  return filters;
};

interface StaffLevelsTableProps {
  onStaffLevelClick: (staffLevel: StaffLevel) => void;
  onDeleteClick: (staffLevel: StaffLevel) => void;
}

const StaffLevelsTable = ({
  onStaffLevelClick,
  onDeleteClick,
}: StaffLevelsTableProps) => {
  const columns = useMemo<MRT_ColumnDef<StaffLevel>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row, renderedCellValue }) => {
          const staffLevel = row.original;
          return (
            <button
              onClick={() => onStaffLevelClick?.(staffLevel)}
              className="text-purple-500 hover:underline cursor-pointer"
            >
              {renderedCellValue}
            </button>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => {
          const staffLevel = row.original;
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDeleteClick(staffLevel)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          );
        },
      },
    ],
    [onStaffLevelClick, onDeleteClick]
  );

  return (
    <>
      <APIPaginatedTable
        columns={columns}
        reactQuery={useGetStaffLevelsPaginated}
        reactQueryFilters={(state: APIPaginatedTableState) => {
          const filters = createStaffLevelsFilters(state.columnFilters);

          return {
            page: state.pagination.pageIndex,
            size: state.pagination.pageSize,
            ...filters,
          };
        }}
        onRenderToolbarInternalActions={({ table }) => (
          <>
            <MRT_ToggleFiltersButton table={table} />
          </>
        )}
        titleTopic="staff levels"
        contentTopic="staff level"
      />
    </>
  );
};

export default StaffLevelsTable;